/**
 * Aspect ratio definitions for image cropping
 * Ported from Android SupportedAspectRatio enum
 */

export interface AspectRatioConfig {
  label: string;
  ratio: number;
  geminiValue: string;
}

/**
 * All supported aspect ratios
 * Ordered by ratio value (horizontal first, then vertical)
 */
export const ASPECT_RATIOS = {
  // Horizontal ratios (ratio > 1)
  RATIO_21_9: { label: '21:9', ratio: 21 / 9, geminiValue: '21:9' },
  RATIO_16_9: { label: '16:9', ratio: 16 / 9, geminiValue: '16:9' },
  RATIO_3_2: { label: '3:2', ratio: 3 / 2, geminiValue: '3:2' },
  RATIO_4_3: { label: '4:3', ratio: 4 / 3, geminiValue: '4:3' },
  RATIO_5_4: { label: '5:4', ratio: 5 / 4, geminiValue: '5:4' },
  // Square
  SQUARE: { label: '1:1', ratio: 1, geminiValue: '1:1' },
  // Vertical ratios (ratio < 1)
  RATIO_4_5: { label: '4:5', ratio: 4 / 5, geminiValue: '4:5' },
  RATIO_3_4: { label: '3:4', ratio: 3 / 4, geminiValue: '3:4' },
  RATIO_2_3: { label: '2:3', ratio: 2 / 3, geminiValue: '2:3' },
  RATIO_9_16: { label: '9:16', ratio: 9 / 16, geminiValue: '9:16' },
} as const;

export type AspectRatioKey = keyof typeof ASPECT_RATIOS;

/**
 * Common aspect ratios shown by default in the selector
 */
export const COMMON_ASPECT_RATIOS: AspectRatioKey[] = [
  'SQUARE',
  'RATIO_4_3',
  'RATIO_3_4',
  'RATIO_16_9',
  'RATIO_9_16',
];

/**
 * All horizontal aspect ratios (ratio > 1)
 */
export const HORIZONTAL_ASPECT_RATIOS: AspectRatioKey[] = [
  'RATIO_5_4',
  'RATIO_4_3',
  'RATIO_3_2',
  'RATIO_16_9',
  'RATIO_21_9',
];

/**
 * All vertical aspect ratios (ratio < 1)
 */
export const VERTICAL_ASPECT_RATIOS: AspectRatioKey[] = [
  'RATIO_4_5',
  'RATIO_3_4',
  'RATIO_2_3',
  'RATIO_9_16',
];

/**
 * Check if ratio is horizontal (landscape)
 */
export function isHorizontalRatio(ratio: number): boolean {
  return ratio > 1;
}

/**
 * Check if ratio is vertical (portrait)
 */
export function isVerticalRatio(ratio: number): boolean {
  return ratio < 1;
}

/**
 * Check if ratio is square
 */
export function isSquareRatio(ratio: number): boolean {
  return Math.abs(ratio - 1) < 0.01;
}

/**
 * Find the best matching aspect ratio for given dimensions
 * Returns the ratio that would maximize the crop area while fitting within the image
 */
export function findBestAspectRatio(
  width: number,
  height: number
): AspectRatioKey {
  const imageRatio = width / height;

  // Find closest matching ratio from common ratios
  let bestMatch: AspectRatioKey = 'SQUARE';
  let smallestDiff = Infinity;

  for (const key of COMMON_ASPECT_RATIOS) {
    const config = ASPECT_RATIOS[key];
    const diff = Math.abs(config.ratio - imageRatio);

    if (diff < smallestDiff) {
      smallestDiff = diff;
      bestMatch = key;
    }
  }

  return bestMatch;
}

/**
 * Find the closest matching aspect ratio from all available ratios
 */
export function findClosestAspectRatio(
  width: number,
  height: number
): AspectRatioKey {
  const imageRatio = width / height;

  let bestMatch: AspectRatioKey = 'SQUARE';
  let smallestDiff = Infinity;

  for (const [key, config] of Object.entries(ASPECT_RATIOS)) {
    const diff = Math.abs(config.ratio - imageRatio);

    if (diff < smallestDiff) {
      smallestDiff = diff;
      bestMatch = key as AspectRatioKey;
    }
  }

  return bestMatch;
}

/**
 * Returns the aspect ratio whose orientation is swapped (RATIO_4_3 → RATIO_3_4).
 * Used after a 90° image rotation in the cropper. Ratios without a strict
 * counterpart (e.g. RATIO_21_9) fall back to the closest available match.
 */
export function invertedAspectRatio(key: AspectRatioKey): AspectRatioKey {
  const ratio = ASPECT_RATIOS[key].ratio;
  return findClosestAspectRatio(1000, Math.round(1000 * ratio));
}

/**
 * Calculate initial crop rect that maximizes the crop area
 * while maintaining the target aspect ratio
 */
export function calculateInitialCropRect(
  imageWidth: number,
  imageHeight: number,
  targetRatio: number
): { x: number; y: number; width: number; height: number } {
  const imageRatio = imageWidth / imageHeight;

  let cropWidth: number;
  let cropHeight: number;

  if (targetRatio > imageRatio) {
    // Target is wider than image - fit to width
    cropWidth = imageWidth;
    cropHeight = imageWidth / targetRatio;
  } else {
    // Target is taller than image - fit to height
    cropHeight = imageHeight;
    cropWidth = imageHeight * targetRatio;
  }

  // Center the crop area
  const x = (imageWidth - cropWidth) / 2;
  const y = (imageHeight - cropHeight) / 2;

  return { x, y, width: cropWidth, height: cropHeight };
}

/**
 * Get aspect ratio config by key
 */
export function getAspectRatioConfig(key: AspectRatioKey): AspectRatioConfig {
  return ASPECT_RATIOS[key];
}

/**
 * Get aspect ratio value by key
 */
export function getAspectRatioValue(key: AspectRatioKey): number {
  return ASPECT_RATIOS[key].ratio;
}
